import { useState, useMemo, useCallback } from "react";
import { X, DollarSign, TrendingUp } from "lucide-react";
import { useJobCostSummaries, useJobCostDetails } from "../../../hooks/usePricing";
import { CostDetailEntry } from "../../../services/pricing.service";
import {
  AccentBarHeading,
  IllustratedEmptyState,
  SummaryStatCard,
} from "@/components/ui/molecules";
import { DataTableOrganism } from "@/components/ui/organisms";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/atoms/Table";
import { SummarizedListSectionTemplate } from "@/components/ui/templates";
import {
  createJobCostTableColumns,
  mapJobCostSummaryToRow,
} from "./jobCostTable.columns";
import { formatDateTime } from "@/utils/date";
import { formatCurrency } from "@/utils/locale";
import { Button } from "@/components/ui/atoms/Button";

const formatUsdCost = (amount: number): string =>
  formatCurrency(amount, 'USD', { minimumFractionDigits: 6, maximumFractionDigits: 6 });

interface JobCostOverviewProps {
  isAdmin: boolean;
  isLoading: boolean;
}

export const JobCostOverview = ({ isAdmin, isLoading }: JobCostOverviewProps) => {
  const [isCostModalOpen, setIsCostModalOpen] = useState(false);
  const [selectedJobIdForCost, setSelectedJobIdForCost] = useState<string | null>(null);

  const {
    data: jobCostSummariesData = [],
    isLoading: isCostsInitialLoading,
    isFetching: isFetchingCosts,
    isError: isCostsQueryError,
    error: costsQueryError,
  } = useJobCostSummaries({ enabled: isAdmin && !isLoading });
  const {
    data: jobCostDetailsData,
    isLoading: isFetchingCostDetails,
    error: costDetailsError,
  } = useJobCostDetails(selectedJobIdForCost, {
    enabled: isCostModalOpen && !!selectedJobIdForCost,
  });

  const handleViewCostDetails = useCallback((jobId: string) => {
    setSelectedJobIdForCost(jobId);
    setIsCostModalOpen(true);
  }, []);

  const handleCloseCostModal = () => {
    setIsCostModalOpen(false);
    setSelectedJobIdForCost(null);
  };

  const totalCost = jobCostSummariesData.reduce((acc, summary) => acc + summary.total_price, 0);

  const tableRows = useMemo(
    () => jobCostSummariesData.map(mapJobCostSummaryToRow),
    [jobCostSummariesData]
  );

  const jobCostColumns = useMemo(
    () =>
      createJobCostTableColumns({
        formatCurrency: formatUsdCost,
        onViewDetails: handleViewCostDetails,
      }),
    [handleViewCostDetails]
  );

  const emptyState = useMemo(() => {
    if (tableRows.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<DollarSign />}
          title="No job cost data available"
          description="There is no job cost data to display yet."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<DollarSign />}
        title="No jobs found matching your search"
        description="Try a different company, email, or job title."
      />
    );
  }, [tableRows.length]);

  if (!isAdmin) {
    return null;
  }

  const costsStatus = isCostsInitialLoading
    ? "loading"
    : isCostsQueryError
      ? "error"
      : "ready";
  const costsErrorMessage =
    costsQueryError instanceof Error ? costsQueryError.message : "Failed to load job costs";

  const refetchIndicator =
    isFetchingCosts && !isCostsInitialLoading ? (
      <div className="flex items-center gap-2 text-sm text-dark-invirtus/60">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-purple border-t-transparent" />
        <span>Updating…</span>
      </div>
    ) : null;

  return (
    <>
      <SummarizedListSectionTemplate
        status={costsStatus}
        loadingMessage="Loading cost data..."
        errorTitle="Error loading job costs"
        errorMessage={costsErrorMessage}
        sectionGap="relaxed"
        summary={
          costsStatus === "ready" ? (
            <SummaryStatCard
              label="Total Platform Cost"
              value={formatUsdCost(totalCost)}
              icon={<TrendingUp className="text-gradient-purple" />}
            />
          ) : undefined
        }
        primary={
          costsStatus === "ready" ? (
            <DataTableOrganism
              columns={jobCostColumns}
              data={tableRows}
              pageSize={10}
              enableSelection
              enableSearch
              enableColumnVisibility
              columnVisibilityLabel="Column visibility"
              toolbarTitle={
                <AccentBarHeading>Job Cost Breakdown</AccentBarHeading>
              }
              searchPlaceholder="Search by company, email, job…"
              searchKeys={["companyName", "creatorEmail", "jobTitle"]}
              paginationItemLabel="jobs"
              toolbarEndSlot={refetchIndicator}
              dataTableProps={{
                emptyState,
              }}
            />
          ) : undefined
        }
      />

      {isCostModalOpen && (
        <div className="fixed inset-0 z-50 flex animate-in items-center justify-center bg-dark-invirtus/80 p-4 fade-in duration-300 backdrop-blur-sm">
          <div className="flex max-h-[85vh] w-full max-w-5xl animate-in flex-col rounded-xl border border-light-lila/30 bg-white shadow-lg slide-in-from-bottom duration-300">
            <div className="rounded-t-2xl p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="flex items-center gap-3 font-heading text-2xl font-bold text-dark-invirtus">
                    Cost Details
                  </h3>
                  <p className="mt-1 text-sm font-medium text-dark-invirtus/70">
                    {jobCostSummariesData.find((j) => j.job_id === selectedJobIdForCost)
                      ?.job_title || selectedJobIdForCost}
                  </p>
                </div>
                <Button
                  type="icon"
                  intent="muted"
                  icon={<X/>}
                  onClick={handleCloseCostModal}
                  label="Close cost details"
                  aria-label="Close cost details"
                />
              </div>
            </div>

            <div className="flex-grow overflow-y-auto">
              {isFetchingCostDetails ? (
                <div className="flex h-60 flex-col items-center justify-center">
                  <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gradient-purple border-t-transparent" />
                  <p className="font-medium text-dark-invirtus/60">Loading detailed costs...</p>
                </div>
              ) : costDetailsError ? (
                <div className="rounded-xl border border-red/30 bg-gradient-to-r from-red/10 to-light-red/20 p-6 text-center">
                  <p className="font-semibold text-dark-invirtus">
                    Error fetching cost details:{" "}
                    {costDetailsError instanceof Error
                      ? costDetailsError.message
                      : "Unknown error"}
                  </p>
                </div>
              ) : jobCostDetailsData && jobCostDetailsData.details.length > 0 ? (
                <div className="space-y-4">
                  <div className="overflow-x-auto border border-light-lila/40">
                    <Table>
                      <TableHeader className="bg-gradient-to-r from-light-lila/20 to-light-purple/10">
                        <TableRow inHeader>
                          <TableHead className="font-heading font-bold text-dark-invirtus">
                            Timestamp
                          </TableHead>
                          <TableHead className="font-heading font-bold text-dark-invirtus">
                            Description
                          </TableHead>
                          <TableHead
                            align="right"
                            className="font-heading font-bold text-dark-invirtus"
                          >
                            Cost
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {jobCostDetailsData.details
                          .sort(
                            (a, b) =>
                              new Date(b.timestamp).getTime() -
                              new Date(a.timestamp).getTime()
                          )
                          .map((detail: CostDetailEntry, index: number) => (
                            <TableRow
                              key={index}
                              className="hover:bg-gradient-to-r hover:from-light-lila/5 hover:to-transparent transition-colors"
                            >
                              <TableCell className="text-dark-invirtus/60">
                                {formatDateTime(detail.timestamp)}
                              </TableCell>
                              <TableCell className="font-medium text-dark-invirtus whitespace-normal">
                                {detail.message}
                              </TableCell>
                              <TableCell
                                align="right"
                                numeric
                                className="font-bold text-purple"
                              >
                                {formatUsdCost(detail.price)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            align="right"
                            className="px-6 py-4 font-heading text-base font-bold uppercase text-dark-invirtus"
                          >
                            Total Cost
                          </TableCell>
                          <TableCell
                            align="right"
                            numeric
                            className="px-6 py-4 font-heading text-xl font-bold text-purple"
                          >
                            {formatUsdCost(jobCostDetailsData.total_price)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="flex h-60 flex-col items-center justify-center">
                  <DollarSign className="mb-4 h-16 w-16 text-dark-invirtus/20" />
                  <p className="font-medium text-dark-invirtus/60">
                    No cost details found for this job.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end p-5">
              <Button
                id="close-cost-modal-btn"
                label="Close"
                intent="muted"
                onClick={handleCloseCostModal}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
